export function normalizeText(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()
}

export function ensureEndsWithPeriod(value: string): string
export function ensureEndsWithPeriod(value: string | null | undefined): string | null | undefined
export function ensureEndsWithPeriod(value: string | null | undefined): string | null | undefined {
  if (value == null) return value

  const trimmed = value.trimEnd()
  if (!trimmed) return trimmed
  if (trimmed.endsWith('.')) return trimmed
  return `${trimmed}.`
}

export function isOutOfNetworkPortfolioName(value: string | null | undefined): boolean {
  if (!value) return false
  return normalizeText(value) === normalizeText('Fora dos portfólios da rede')
}

const MONTHS_PT = [
  'janeiro',
  'fevereiro',
  'março',
  'abril',
  'maio',
  'junho',
  'julho',
  'agosto',
  'setembro',
  'outubro',
  'novembro',
  'dezembro',
]

export function formatMonthYearPt(params: { year: string | number; month: number }): string {
  const monthIndex = params.month >= 1 && params.month <= 12 ? params.month - 1 : params.month
  const monthName = MONTHS_PT[monthIndex] ?? String(params.month)
  return `${monthName} de ${params.year}`
}

export function formatListPt(
  items: string[],
  options: {
    maxItems?: number
  } = {},
): string {
  const maxItems = options.maxItems ?? Infinity
  const clean = items.map(item => item.trim()).filter(Boolean)
  if (clean.length === 0) return ''

  const shown = clean.slice(0, maxItems)
  const remaining = clean.length - shown.length
  const finalItems = remaining > 0 ? [...shown, `mais ${remaining}`] : shown

  if (finalItems.length === 1) return finalItems[0]
  if (finalItems.length === 2) return `${finalItems[0]} e ${finalItems[1]}`

  const head = finalItems.slice(0, -1).join(', ')
  const last = finalItems[finalItems.length - 1]
  return `${head} e ${last}`
}

export function formatQuotedListPt(
  items: string[],
  options: {
    maxItems?: number
    quote?: '"' | '“' | '”' | "'"
  } = {},
): string {
  const quote = options.quote ?? '"'
  return formatListPt(
    items.map(item => `${quote}${item}${quote}`),
    { maxItems: options.maxItems },
  )
}

export function formatCountWithOptionalListPt(params: {
  count: number
  singular: string
  plural: string
  items?: string[]
  maxItems?: number
}): string {
  const label = params.count === 1 ? params.singular : params.plural
  const base = `${params.count} ${label}`

  const items = params.items?.map(item => item.trim()).filter(Boolean) ?? []
  if (!items.length) return base

  const list = formatListPt(items, { maxItems: params.maxItems })
  return list ? `${base} (${list})` : base
}

const BR_UFS = [
  'AC',
  'AL',
  'AP',
  'AM',
  'BA',
  'CE',
  'DF',
  'ES',
  'GO',
  'MA',
  'MT',
  'MS',
  'MG',
  'PA',
  'PB',
  'PR',
  'PE',
  'PI',
  'RJ',
  'RN',
  'RS',
  'RO',
  'RR',
  'SC',
  'SP',
  'SE',
  'TO',
] as const

export function formatUfScopePt(params: {
  included: string[]
  all?: string[]
  exceptUpTo?: number
}): string {
  const normalizeUf = (value: string) => value.trim().toUpperCase()

  const allUfs = Array.from(new Set((params.all ?? BR_UFS).map(normalizeUf).filter(Boolean))).sort()
  const includedUfs = Array.from(new Set(params.included.map(normalizeUf).filter(Boolean))).sort()
  const allSet = new Set(allUfs)
  const includedSet = new Set(includedUfs)

  const totalCount = allUfs.length
  const includedCount = includedUfs.length

  if (
    totalCount > 0 &&
    includedCount === totalCount &&
    allUfs.every(uf => includedSet.has(uf)) &&
    includedUfs.every(uf => allSet.has(uf))
  ) {
    return 'todas as UFs'
  }

  const missingUfs = allUfs.filter(uf => !includedSet.has(uf))
  const exceptUpTo = params.exceptUpTo ?? 6
  if (totalCount > 0 && missingUfs.length > 0 && missingUfs.length <= exceptUpTo) {
    const missingList = formatListPt(missingUfs, { maxItems: Infinity })
    return missingList ? `todas as UFs exceto ${missingList}` : 'todas as UFs'
  }

  return formatCountWithOptionalListPt({
    count: includedCount,
    singular: 'UF',
    plural: 'UFs',
    items: includedUfs,
    maxItems: Infinity,
  })
}

export function formatPortfolioClausePt(portfolioNames: string[] | null | undefined): string {
  const names = (portfolioNames ?? []).map(name => name.trim()).filter(Boolean)
  if (!names.length) return ''

  const unique = Array.from(new Map(names.map(name => [normalizeText(name), name])).values())
  const effective = unique.filter(name => !isOutOfNetworkPortfolioName(name))

  if (!effective.length) return ''
  if (effective.length === 1) return `, pertencente ao portfólio "${effective[0]}",`

  return `, pertencente aos portfólios ${formatQuotedListPt(effective, { quote: '"' })},`
}
